import { c as _c } from "react/compiler-runtime";
import { useLocale } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { useDebounceCallback, useMediaQuery } from 'usehooks-ts';
import { SEARCH_PARAM_KEYS, SEARCH_RECOMMENDED_LIMIT } from '@/constants/search';
import { useConfigsContext } from '@/context/ConfigsContext';
import { useUAContext } from '@/context/UAContext';
import { usePathname } from '@/i18n/navigation';
import { adaptDeviceType, convertToDeviceType, createSuggestionWorlds } from '@/lib/search';
import { getRecentSearch, getRecommendWorlds, getRoomsAutocomplete, patchRecentSearch } from '@/services/cms';
export type SuggestionWorld = {
  id: string;
  title: string;
};
export function useWorldFuzzySearch(isSearchSidebarOpen) {
  const $ = _c(42);
  const {
    store
  } = useConfigsContext();
  const {
    deviceTypeWithAndroidAndIOS
  } = useUAContext();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.get(SEARCH_PARAM_KEYS.KEYWORD) ?? "";
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const search = t0;
  const [searchValue, setSearchValue] = useState(search);
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(search);
  const [isClearingRecentSearch, setIsClearingRecentSearch] = useState(false);
  const debounced = useDebounceCallback(setDebouncedSearchValue, 300);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isSearchPage = pathname === "/search";
  const locale = useLocale();
  const htcToken = store?.credentials?.htcToken;
  const hasSearchValue = debouncedSearchValue.length > 2;
  let t1;
  if ($[2] !== debouncedSearchValue || $[3] !== isDesktop || $[4] !== isSearchPage || $[5] !== isSearchSidebarOpen) {
    t1 = (isDesktop && isSearchSidebarOpen || isSearchPage) && debouncedSearchValue.trim().length >= 3;
    $[2] = debouncedSearchValue;
    $[3] = isDesktop;
    $[4] = isSearchPage;
    $[5] = isSearchSidebarOpen;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const shouldFetchAutocomplete = t1;
  let t2;
  let t3;
  if ($[7] !== debounced || $[8] !== searchValue) {
    t2 = () => {
      debounced(searchValue);
    };
    t3 = [searchValue, debounced];
    $[7] = debounced;
    $[8] = searchValue;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[11] !== search) {
    t4 = () => {
      setSearchValue(search);
    };
    t5 = [search];
    $[11] = search;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  if ($[14] !== debouncedSearchValue || $[15] !== shouldFetchAutocomplete) {
    t6 = shouldFetchAutocomplete ? ["getRoomsAutocomplete", debouncedSearchValue] : null;
    $[14] = debouncedSearchValue;
    $[15] = shouldFetchAutocomplete;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      revalidateOnFocus: false
    };
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const {
    data: t8,
    isLoading: isLoadingAutocomplete
  } = useSWR(t6, _temp, t7);
  const autocompleteKeywords = t8 === undefined ? [] : t8;
  let t9;
  if ($[18] !== deviceTypeWithAndroidAndIOS || $[19] !== htcToken || $[20] !== locale) {
    t9 = () => {
      const deviceType = convertToDeviceType(deviceTypeWithAndroidAndIOS);
      const adaptedDeviceType = adaptDeviceType(deviceType, deviceTypeWithAndroidAndIOS);
      return getRecommendWorlds(htcToken, {
        locale,
        device: adaptedDeviceType,
        limit: SEARCH_RECOMMENDED_LIMIT
      }).then(_temp2);
    };
    $[18] = deviceTypeWithAndroidAndIOS;
    $[19] = htcToken;
    $[20] = locale;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      revalidateOnFocus: false
    };
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const {
    data: recommendWorlds
  } = useSWR(isSearchPage ? "getSidebarRecommendWorlds" : null, t9, t10);
  let t11;
  if ($[23] !== htcToken) {
    t11 = async () => {
      const response_0 = await getRecentSearch(htcToken, false);
      return createSuggestionWorlds(response_0?.results?.keywords ?? [], "recent");
    };
    $[23] = htcToken;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      revalidateOnFocus: false
    };
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const {
    data: t13,
    mutate: mutateRecentSearch,
    isValidating: isValidatingRecentSearch
  } = useSWR(htcToken && (isDesktop || isSearchPage) ? `getRecentSearch-${search}` : null, t11, t12);
  let t14;
  if ($[26] !== t13) {
    t14 = t13 === undefined ? [] : t13;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const recentSearches = t14;
  let t15;
  if ($[28] !== htcToken || $[29] !== mutateRecentSearch || $[30] !== recentSearches) {
    t15 = async id => {
      if (!htcToken || !recentSearches) {
        return;
      }
      setIsClearingRecentSearch(true);
      const updatedRecentSearches = recentSearches.filter(search_0 => search_0.id !== id);
      const keywords = updatedRecentSearches.map(_temp3);
      await patchRecentSearch(htcToken, {
        keywords
      });
      await mutateRecentSearch();
      setIsClearingRecentSearch(false);
    };
    $[28] = htcToken;
    $[29] = mutateRecentSearch;
    $[30] = recentSearches;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const clearRecentSearches = t15;
  let t16;
  if ($[32] !== recentSearches) {
    t16 = worlds => {
      const uniqueWorlds = new Set();
      const recentSearchesSet = new Set(recentSearches?.map(_temp4));
      return worlds.filter(world => {
        if (recentSearchesSet.has(world.title)) {
          return false;
        }
        if (uniqueWorlds.has(world.title)) {
          return false;
        }
        uniqueWorlds.add(world.title);
        return true;
      });
    };
    $[32] = recentSearches;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const filterDuplicateWorldsOnRecentSearch = t16;
  const rawSuggestionWorlds = hasSearchValue ? createSuggestionWorlds(autocompleteKeywords) : (recommendWorlds || []).map(_temp5);
  const suggestionWorlds = filterDuplicateWorldsOnRecentSearch(rawSuggestionWorlds);
  const isLoadingRecentSearch = isValidatingRecentSearch || isClearingRecentSearch;
  let t17;
  if ($[34] !== clearRecentSearches || $[35] !== debouncedSearchValue || $[36] !== isLoadingAutocomplete || $[37] !== isLoadingRecentSearch || $[38] !== recentSearches || $[39] !== searchValue || $[40] !== suggestionWorlds) {
    t17 = {
      searchValue,
      setSearchValue,
      debouncedSearchValue,
      recentSearches,
      suggestionWorlds,
      clearRecentSearches,
      isLoadingAutocomplete,
      isLoadingRecentSearch
    };
    $[34] = clearRecentSearches;
    $[35] = debouncedSearchValue;
    $[36] = isLoadingAutocomplete;
    $[37] = isLoadingRecentSearch;
    $[38] = recentSearches;
    $[39] = searchValue;
    $[40] = suggestionWorlds;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  return t17;
}
function _temp5(room) {
  return {
    id: room.id,
    title: room.title
  };
}
function _temp4(search_2) {
  return search_2.title;
}
function _temp3(search_1) {
  return search_1.title;
}
function _temp2(res) {
  return res?.results.rooms;
}
async function _temp(t0) {
  const [, keyword] = t0;
  const response = await getRoomsAutocomplete(keyword, false);
  return response?.results?.keywords;
}